import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ByodBanner } from "@/components/ByodBanner";
import { DashboardQuickActions } from "@/components/dashboard/DashboardQuickActions";
import { OperationalMetric } from "@/components/dashboard/OperationalMetric";
import {
  Users,
  Bot,
  Globe,
  Activity,
  TrendingUp,
  Server,
  AlertTriangle,
  RefreshCw,
  Timer
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeasuredChart = ({ children, testId }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const updateWidth = () => setWidth(Math.max(0, Math.floor(element.getBoundingClientRect().width)));
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} data-testid={testId} className="h-64 min-w-0 w-full" style={{ minHeight: "256px" }}>
      {width > 0
        ? (typeof children === "function" ? children(width) : children)
        : <div className="h-full w-full animate-pulse rounded bg-slate-800/40" />}
    </div>
  );
};

// Trial/Subscription Countdown Component
const TrialCountdown = ({ token }) => {
  const [trialStatus, setTrialStatus] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    const fetchTrialStatus = async () => {
      try {
        const response = await axios.get(`${API}/user/trial-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrialStatus(response.data);
        // Use trial time for trials, subscription time for subscriptions
        const remaining = response.data.is_on_trial 
          ? response.data.remaining_seconds 
          : response.data.subscription_remaining_seconds;
        setRemainingTime(remaining || 0);
      } catch (error) {
        console.error("Error fetching trial status:", error);
      }
    };

    fetchTrialStatus();
    const statusInterval = setInterval(fetchTrialStatus, 15000); // Refresh every 15s

    // Instantly refresh when a billing update arrives (payment approval, etc.)
    window.addEventListener("billing:update", fetchTrialStatus);
    window.addEventListener("billing:poll", fetchTrialStatus);

    return () => {
      clearInterval(statusInterval);
      window.removeEventListener("billing:update", fetchTrialStatus);
      window.removeEventListener("billing:poll", fetchTrialStatus);
    };
  }, [token]);

  // Countdown timer
  useEffect(() => {
    if (remainingTime <= 0) return;
    if (trialStatus?.trial_paused) return; // Frozen by admin — don't tick down
    
    const timer = setInterval(() => {
      setRemainingTime(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [remainingTime, trialStatus?.trial_paused]);

  // Don't show if no active trial or subscription
  const isOnTrial = trialStatus?.is_on_trial;
  const hasSubscription = trialStatus?.subscription_end && !isOnTrial;
  
  if (!isOnTrial && !hasSubscription) return null;

  const days = Math.floor(remainingTime / 86400);
  const hours = Math.floor((remainingTime % 86400) / 3600);
  const minutes = Math.floor((remainingTime % 3600) / 60);
  const seconds = remainingTime % 60;
  
  // Different thresholds for trial vs subscription
  const isExpiringSoon = isOnTrial ? remainingTime <= 3600 : remainingTime <= 86400; // 1h for trial, 1d for subscription
  const isUrgent = isOnTrial ? remainingTime <= 1800 : remainingTime <= 3600; // 30m for trial, 1h for subscription

  const label = isOnTrial ? 'Trial' : trialStatus?.subscription_plan_name || 'Subscription';
  const isPaused = trialStatus?.trial_paused;

  return (
    <div 
      className={`relative overflow-hidden rounded-md border p-4 mb-6 ${
        isUrgent 
          ? 'border-red-500/40 bg-red-500/10' 
          : isExpiringSoon 
            ? 'border-yellow-500/40 bg-yellow-500/10'
            : 'border-cyan-500/30 bg-[#0c121d]'
      }`}
      data-testid="trial-countdown-banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isUrgent ? 'bg-red-500/20' : isExpiringSoon ? 'bg-yellow-500/20' : 'bg-cyan-500/20'}`}>
            <Timer className={`w-6 h-6 ${isUrgent ? 'text-red-400' : isExpiringSoon ? 'text-yellow-400' : 'text-cyan-400'}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${isUrgent ? 'text-red-400' : isExpiringSoon ? 'text-yellow-400' : 'text-cyan-400'}`}>
              {isPaused ? `${label} Paused` : isUrgent ? `${label} Almost Over` : isExpiringSoon ? `${label} Expiring Soon` : `${label} Active`}
            </h3>
            <p className="text-sm text-slate-400">
              {isPaused
                ? `Your countdown is temporarily frozen by an admin. Time will resume shortly.`
                : remainingTime <= 0 
                ? `Your ${label.toLowerCase()} has expired. Go to billing to renew.`
                : `Time remaining in your ${label.toLowerCase()}`
              }
            </p>
          </div>
        </div>
        
        {remainingTime > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 font-mono text-2xl font-bold">
              {days > 0 && (
                <>
                  <div className={`px-3 py-2 rounded-lg ${isUrgent ? 'bg-red-500/20 text-red-400' : isExpiringSoon ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-800 text-white'}`}>
                    {days}d
                  </div>
                  <span className="text-slate-500">:</span>
                </>
              )}
              <div className={`px-3 py-2 rounded-lg ${isUrgent ? 'bg-red-500/20 text-red-400' : isExpiringSoon ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-800 text-white'}`}>
                {String(hours).padStart(2, '0')}
              </div>
              <span className="text-slate-500">:</span>
              <div className={`px-3 py-2 rounded-lg ${isUrgent ? 'bg-red-500/20 text-red-400' : isExpiringSoon ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-800 text-white'}`}>
                {String(minutes).padStart(2, '0')}
              </div>
              <span className="text-slate-500">:</span>
              <div className={`px-3 py-2 rounded-lg ${isUrgent ? 'bg-red-500/20 text-red-400' : isExpiringSoon ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-800 text-white'}`}>
                {String(seconds).padStart(2, '0')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [liveVisitors, setLiveVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  const fetchLiveVisitors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/live-visitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveVisitors(response.data.visitors);
    } catch (error) {
      console.error("Error fetching live visitors:", error);
    }
  }, [token]);

  const clearStats = async () => {
    if (!window.confirm("Are you sure you want to clear all stats? This will reset:\n• Total Visits\n• Human Visits\n• Bot Blocked\n• Today's stats\n\nThis action cannot be undone!")) {
      return;
    }
    setClearing(true);
    try {
      await axios.post(`${API}/dashboard/clear-stats`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchStats();
      alert("Stats cleared successfully!");
    } catch (error) {
      console.error("Error clearing stats:", error);
      alert(error.response?.data?.detail || "Failed to clear stats");
    }
    setClearing(false);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchLiveVisitors()]);
      setLoading(false);
    };
    loadData();

    // Auto-refresh stats and live visitors every 15 seconds
    const statsInterval = setInterval(fetchStats, 15000);
    const visitorsInterval = setInterval(fetchLiveVisitors, 15000);
    return () => {
      clearInterval(statsInterval);
      clearInterval(visitorsInterval);
    };
  }, [fetchStats, fetchLiveVisitors]);

  const COLORS = ["#06b6d4", "#14b8a6", "#10b981", "#f59e0b", "#ef4444"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="modern-dashboard-command-center">
      {/* Trial Countdown Banner */}
      <TrialCountdown token={token} />

      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-[#1e2b3e] pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase text-cyan-400">Operational command center</p>
          <h1 className="mt-1 text-2xl font-orbitron font-bold text-white md:text-3xl" data-testid="dashboard-title">
            Welcome, <span className="text-cyan-300">{user?.username || "User"}</span>
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Live campaign traffic, defenses, and routing performance.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={clearStats}
            disabled={clearing}
            className="btn-secondary flex items-center gap-2 text-red-400 border-red-500/30 hover:bg-red-500/10"
            data-testid="clear-stats-btn"
          >
            <AlertTriangle className={`w-4 h-4 ${clearing ? "animate-pulse" : ""}`} />
            {clearing ? "Clearing..." : "Clear Stats"}
          </button>
          <button
            onClick={fetchStats}
            disabled={refreshing}
            className="btn-secondary flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Bring Your Own Domain discovery banner (licensed users w/o a custom domain) */}
      <ByodBanner token={token} />

      <DashboardQuickActions onNavigate={navigate} />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="primary-metrics-grid">
        <OperationalMetric
          icon={Activity}
          label="Total Visits"
          value={stats?.total_visits}
          detail="All-time requests"
          tone="cyan"
        />
        <OperationalMetric
          icon={Users}
          label="Human Visits"
          value={stats?.human_visits}
          detail="Verified human traffic"
          tone="emerald"
        />
        <OperationalMetric
          icon={Bot}
          label="Bot Blocked"
          value={stats?.bot_visits}
          detail="Automated requests stopped"
          tone="rose"
        />
        <OperationalMetric
          icon={TrendingUp}
          label="Today"
          value={stats?.today_visits}
          detail="Requests in the last 24 hours"
          tone="teal"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="secondary-metrics-grid">
        <OperationalMetric
          icon={Server}
          label="Active Domains"
          value={stats?.active_domains}
          detail="Domains serving traffic"
          tone="teal"
        />
        <OperationalMetric
          icon={AlertTriangle}
          label="Blocked IPs"
          value={stats?.blocked_ips}
          detail="Active denylist entries"
          tone="amber"
        />
        <OperationalMetric
          icon={Globe}
          label="Allowed Countries"
          value={stats?.allowed_countries}
          detail="Countries permitted by policy"
          tone="cyan"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Traffic Chart */}
        <div className="border border-[#1e2b3e] bg-[#0c121d]/90 p-5 rounded-md" data-testid="hourly-chart">
          <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Hourly Traffic</h3>
          <MeasuredChart testId="hourly-traffic-chart-frame">
            {stats?.hourly_stats && stats.hourly_stats.length > 0 ? (
              (width) => <AreaChart width={width} height={256} data={stats.hourly_stats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="hour" stroke="#94a3b8" tick={{ fill: "#94a3b8" }} />
                  <YAxis stroke="#94a3b8" tick={{ fill: "#94a3b8" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: "8px"
                    }}
                    labelStyle={{ color: "#f8fafc" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#06b6d4"
                    fillOpacity={0.12}
                    fill="#06b6d4"
                    strokeWidth={2}
                  />
                </AreaChart>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">No data available</div>
            )}
          </MeasuredChart>
        </div>

        {/* Country Distribution */}
        <div className="border border-[#1e2b3e] bg-[#0c121d]/90 p-5 rounded-md" data-testid="country-chart">
          <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Top Countries</h3>
          <MeasuredChart testId="country-chart-frame">
            {stats?.country_stats && stats.country_stats.length > 0 ? (
              (width) => <BarChart width={width} height={256} data={stats.country_stats} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                  <XAxis type="number" stroke="#94a3b8" tick={{ fill: "#94a3b8" }} />
                  <YAxis
                    dataKey="country"
                    type="category"
                    stroke="#94a3b8"
                    tick={{ fill: "#94a3b8" }}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: "8px"
                    }}
                    labelStyle={{ color: "#f8fafc" }}
                  />
                  <Bar dataKey="count" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                </BarChart>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">No data available</div>
            )}
          </MeasuredChart>
        </div>
      </div>

      {/* ISP Distribution & Live Visitors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ISP Distribution */}
        <div className="border border-[#1e2b3e] bg-[#0c121d]/90 p-5 rounded-md" data-testid="isp-chart">
          <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Top ISPs</h3>
          <MeasuredChart testId="isp-chart-frame">
            {stats?.isp_stats && stats.isp_stats.length > 0 ? (
              (width) => <PieChart width={width} height={256}>
                  <Pie
                    data={stats.isp_stats}
                    dataKey="count"
                    nameKey="isp"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: "#64748b" }}
                  >
                    {stats.isp_stats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: "8px"
                    }}
                    labelStyle={{ color: "#f8fafc" }}
                  />
                </PieChart>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">No data available</div>
            )}
          </MeasuredChart>
        </div>

        {/* Live Visitors */}
        <div className="border border-[#1e2b3e] bg-[#0c121d]/90 p-5 rounded-md" data-testid="live-visitors">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white font-orbitron">Live Visitors</h3>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-sm text-slate-400">{liveVisitors.length} active</span>
            </div>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {liveVisitors.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No recent visitors in the last 5 minutes.</p>
            ) : (
              liveVisitors.map((visitor, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      visitor.status === "OK" || visitor.status === "REDIRECTED" ? "bg-green-500" : "bg-red-500"
                    }`}></div>
                    <div>
                      <p className="text-sm font-mono text-slate-300">{visitor.ip}</p>
                      <p className="text-xs text-slate-500">{visitor.country}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    visitor.status === "OK" || visitor.status === "REDIRECTED"
                      ? "badge-success"
                      : "badge-danger"
                  }`}>
                    {visitor.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
